// client/src/components/SettingsModal.tsx

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User } from '@supabase/supabase-js';
import { useT } from '../lib/i18n';
import { useDataSharingConsent } from '../hooks/useDataSharingConsent';

interface Props {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
}

export function SettingsModal({ user, isOpen, onClose }: Props) {
  const { t } = useT();

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="settings-modal-backdrop"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="settings-modal" role="dialog" aria-modal="true">
        <div className="settings-modal-header">
          <h2 className="settings-modal-title">{t('settings.title')}</h2>
          <button
            type="button"
            className="settings-modal-close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </div>

        <div className="settings-modal-body">
          <PrivacySection user={user} />
          {/* Future sections:
              <AccountSection user={user} />
              <NotificationsSection user={user} />
              <PreferencesSection user={user} />
          */}
        </div>
      </div>
    </div>
  );

  // Render directly into document.body — bypasses any ancestor stacking contexts
  return createPortal(modalContent, document.body);
}

// ─────────────────────────────────────────────────────────────────────────────
// Privacy & Data section
// ─────────────────────────────────────────────────────────────────────────────

function PrivacySection({ user }: { user: User | null }) {
  const { t } = useT();
  const { consent, loading, updating, error, grant, revoke } = useDataSharingConsent(user);

  const handleToggle = async () => {
    if (updating || loading) return;

    if (consent === true) {
      // Revoking — ask if they also want to delete historical data
      const deleteHistory = window.confirm(
        t('settings.privacy.revokeConfirmMessage')
      );
      await revoke(deleteHistory);
    } else {
      await grant();
    }
  };

  return (
    <section className="settings-section">
      <header className="settings-section-header">
        <h3 className="settings-section-title">{t('settings.privacy.title')}</h3>
        <p className="settings-section-description">{t('settings.privacy.description')}</p>
      </header>

      <div className="settings-row">
        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={consent === true}
            disabled={updating || loading || consent === null}
            onChange={handleToggle}
          />
          <div className="settings-toggle-text">
            <span className="settings-toggle-label">{t('settings.privacy.shareStatsLabel')}</span>
            <span className="settings-toggle-help">{t('settings.privacy.shareStatsHelp')}</span>
          </div>
        </label>

        {updating && (
          <span className="settings-status-saving">{t('settings.savingChanges')}</span>
        )}

        {error && <div className="settings-error">{error}</div>}
      </div>

      <details className="settings-detail-block">
        <summary>{t('settings.privacy.whatIsSharedSummary')}</summary>
        <ul className="settings-detail-list">
          <li>{t('settings.privacy.sharedItem1')}</li>
          <li>{t('settings.privacy.sharedItem2')}</li>
          <li>{t('settings.privacy.sharedItem3')}</li>
          <li>{t('settings.privacy.sharedItem4')}</li>
        </ul>
      </details>
    </section>
  );
}
